import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";

import logo from "../assets/images/logo.png";
import bossie from "../assets/images/bossie.png";
import appstore from "../assets/images/appstore.png";
import playstore from "../assets/images/playstore.png";
import {
  FiFacebook,
  FiInstagram,
  FiLinkedin,
  FiTrendingUp,
  FiShield,
  FiMessageCircle,
} from "react-icons/fi";
import { TbBrandTiktok } from "react-icons/tb";
import { Link as Link1 } from "react-scroll";

export default function Footer() {
  const { t } = useTranslation();
  const logoRef = useRef<HTMLImageElement>(null);
  const bossieRef = useRef<HTMLImageElement>(null);

  // Force Safari to re-render images after load
  useEffect(() => {
    const forceRepaint = (img: HTMLImageElement | null) => {
      if (img && img.complete) {
        // Force a repaint by toggling a tiny transform
        img.style.transform = "translateZ(0) scale(1.0001)";
        requestAnimationFrame(() => {
          if (img) {
            img.style.transform = "";
          }
        });
      }
    };

    const handleLoad = () => {
      forceRepaint(logoRef.current);
      forceRepaint(bossieRef.current);
    };

    // Check if images are already loaded
    if (logoRef.current?.complete && bossieRef.current?.complete) {
      handleLoad();
    } else {
      window.addEventListener("load", handleLoad);
      return () => window.removeEventListener("load", handleLoad);
    }
  }, []);

  return (
    <footer className="py-8 md:py-10 bg-primaryFaint">
      <div className="container mx-auto px-4">
        {/* Top Section - Logo and Description */}
        <div className="text-center mb-6 md:mb-8">
          <Link to="#" className="logo-footer inline-block mb-3 md:mb-4 group">
            <div className="relative overflow-visible safari-image-fix">
              {/* Default Logo */}
              <div
                className="inline-block h-10 md:h-12"
                style={{ width: "auto" }}
              >
                <img
                  ref={logoRef}
                  src={logo}
                  className="h-full w-auto rounded transition-all duration-500 ease-in-out group-hover:opacity-0 group-hover:scale-110"
                  alt="Bossie Logo"
                  decoding="async"
                  loading="eager"
                  onLoad={(e) => {
                    // Force Safari repaint on load
                    const img = e.currentTarget;
                    img.style.transform = "translateZ(0)";
                    requestAnimationFrame(() => {
                      img.style.transform = "";
                    });
                  }}
                  style={{
                    display: "block",
                    maxWidth: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
              {/* Bossie Logo on Hover */}
              <div
                className="absolute top-0 -left-12 h-8 md:h-10"
                style={{ minWidth: "fit-content", width: "auto" }}
              >
                <img
                  ref={bossieRef}
                  src={bossie}
                  className="h-full w-auto rounded transition-all duration-700 ease-out opacity-0 group-hover:opacity-100 group-hover:scale-100 scale-50 ml-5"
                  alt="Bossie"
                  decoding="async"
                  loading="eager"
                  onLoad={(e) => {
                    // Force Safari repaint on load
                    const img = e.currentTarget;
                    img.style.transform = "translateZ(0)";
                    requestAnimationFrame(() => {
                      img.style.transform = "";
                    });
                  }}
                  style={{
                    display: "block",
                    maxWidth: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
            </div>
          </Link>
          <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 leading-relaxed mb-4 md:mb-6 max-w-3xl mx-auto px-2">
            {t("footer.description")}
          </p>

          {/* Key Features */}
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-6 md:mb-8">
            <div className="flex items-center text-xs md:text-sm text-primaryColor">
              <FiShield className="h-3 w-3 md:h-4 md:w-4 mr-2 md:mr-3 text-primaryColor" />
              <span>{t("footer.secure")}</span>
            </div>
            <div className="flex items-center text-xs md:text-sm text-primaryColor">
              <FiMessageCircle className="h-3 w-3 md:h-4 md:w-4 mr-2 md:mr-3 text-primaryColor" />
              <span>{t("footer.aiNetworking")}</span>
            </div>
            <div className="flex items-center text-xs md:text-sm text-primaryColor">
              <FiTrendingUp className="h-3 w-3 md:h-4 md:w-4 mr-2 md:mr-3 text-primaryColor" />
              <span>{t("footer.businessGrowth")}</span>
            </div>
          </div>
        </div>

        {/* Main Footer Content - Download Section on Left, Links on Right */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-12 mb-6 md:mb-8">
          {/* Download Section - Left Side */}
          <div className="lg:col-span-1">
            <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-2 md:mb-3 text-center lg:text-left">
              {t("footer.readyToTransform")}
            </h3>
            <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mb-4 md:mb-6 text-center lg:text-left px-2 lg:px-0">
              {t("footer.joinThousands")}
            </p>

            {/* Download Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center lg:justify-start">
              <Link
                to="#"
                className="inline-flex items-center px-2 md:px-4 py-1.5 md:py-2.5 bg-black text-white rounded-lg hover:bg-gray-900 transition-all duration-200 ease-in-out shadow-lg hover:shadow-xl w-full sm:w-auto justify-center transform hover:-translate-y-0.5 hover:scale-[1.02] active:scale-[0.98]"
              >
                <img
                  src={appstore}
                  alt="App Store"
                  className="h-8 md:h-10 w-auto mr-2 md:mr-3"
                />
                <div className="text-left">
                  <div className="text-xs opacity-75">
                    {t("footer.downloadAppStore")}
                  </div>
                  <div className="text-sm font-semibold">
                    {t("footer.appStore")}
                  </div>
                </div>
              </Link>

              <Link
                to="#"
                className="inline-flex items-center px-2 md:px-4 py-1.5 md:py-2.5 bg-black text-white rounded-lg hover:bg-gray-900 transition-all duration-200 ease-in-out shadow-lg hover:shadow-xl w-full sm:w-auto justify-center transform hover:-translate-y-0.5 hover:scale-[1.02] active:scale-[0.98]"
              >
                <img
                  src={playstore}
                  alt="Google Play"
                  className="h-8 md:h-10 w-auto mr-2 md:mr-3"
                />
                <div className="text-left">
                  <div className="text-xs opacity-75">
                    {t("footer.getItOn")}
                  </div>
                  <div className="text-sm font-semibold">
                    {t("footer.googlePlay")}
                  </div>
                </div>
              </Link>
            </div>
          </div>

          {/* Links Section - Right Side */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {/* Platform Links */}
              <div className="text-center sm:text-left">
                <h4 className="text-sm md:text-base font-semibold text-gray-900 dark:text-white mb-3 md:mb-4">
                  {t("footer.platform")}
                </h4>
                <div className="space-y-2 md:space-y-3">
                  <Link1
                    to="features"
                    className="text-gray-600 dark:text-gray-400 hover:text-primaryColor dark:hover:text-primaryColor transition-colors duration-200 text-xs md:text-sm block"
                  >
                    {t("footer.features")}
                  </Link1>
                  <Link1
                    to="screenshot"
                    className="text-gray-600 dark:text-gray-400 hover:text-primaryColor dark:hover:text-primaryColor transition-colors duration-200 text-xs md:text-sm block"
                  >
                    {t("footer.screenshots")}
                  </Link1>
                  <Link1
                    to="faqs"
                    className="text-gray-600 dark:text-gray-400 hover:text-primaryColor dark:hover:text-primaryColor transition-colors duration-200 text-xs md:text-sm block"
                  >
                    {t("footer.faq")}
                  </Link1>
                </div>
              </div>

              {/* Support */}
              <div className="text-center sm:text-left">
                <h4 className="text-sm md:text-base font-semibold text-gray-900 dark:text-white mb-3 md:mb-4">
                  {t("footer.support")}
                </h4>
                <div className="space-y-2 md:space-y-3">
                  <Link
                    to="mailto:support@bossie.com"
                    className="text-gray-600 dark:text-gray-400 hover:text-primaryColor dark:hover:text-primaryColor transition-colors duration-200 text-xs md:text-sm block"
                  >
                    support@bossie.app
                  </Link>
                  <p className="text-gray-600 dark:text-gray-400 text-xs md:text-sm">
                    {t("footer.customerSupport")}
                  </p>
                </div>
              </div>

              {/* Legal */}
              <div className="text-center sm:text-left">
                <h4 className="text-sm md:text-base font-semibold text-gray-900 dark:text-white mb-3 md:mb-4">
                  {t("footer.legal")}
                </h4>
                <div className="space-y-2 md:space-y-3">
                  <Link
                    to="/about-app"
                    className="text-gray-600 dark:text-gray-400 hover:text-primaryColor dark:hover:text-primaryColor transition-colors duration-200 text-xs md:text-sm block"
                  >
                    {t("footer.aboutBossie")}
                  </Link>
                  <Link
                    to="/privacy-policy"
                    className="text-gray-600 dark:text-gray-400 hover:text-primaryColor dark:hover:text-primaryColor transition-colors duration-200 text-xs md:text-sm block"
                  >
                    {t("footer.privacyPolicy")}
                  </Link>
                  <Link
                    to="/terms-conditions"
                    className="text-gray-600 dark:text-gray-400 hover:text-primaryColor dark:hover:text-primaryColor transition-colors duration-200 text-xs md:text-sm block"
                  >
                    {t("footer.termsConditions")}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 md:pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-2 md:space-y-0">
            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 text-center md:text-left">
              © {new Date().getFullYear()} Bossie. {t("footer.copyright")}
            </p>
            <div className="flex space-x-3 md:space-x-4 justify-center lg:justify-start md:mt-6">
              <Link
                to="https://linkedin.com/company/bossie"
                target="_blank"
                className="size-10 md:size-12 inline-flex items-center justify-center border border-gray-300 dark:border-gray-600 hover:border-primaryColor rounded-lg text-gray-600 dark:text-gray-400 hover:text-white hover:bg-primaryColor transition-all duration-200 hover:shadow-md"
              >
                <FiLinkedin
                  className="h-4 w-4 md:h-5 md:w-5"
                  title="LinkedIn"
                />
              </Link>
              <Link
                to="https://www.facebook.com/bossie"
                target="_blank"
                className="size-10 md:size-12 inline-flex items-center justify-center border border-gray-300 dark:border-gray-600 hover:border-primaryColor rounded-lg text-gray-600 dark:text-gray-400 hover:text-white hover:bg-primaryColor transition-all duration-200 hover:shadow-md"
              >
                <FiFacebook
                  className="h-4 w-4 md:h-5 md:w-5"
                  title="Facebook"
                />
              </Link>
              <Link
                to="https://www.instagram.com/bossie"
                target="_blank"
                className="size-10 md:size-12 inline-flex items-center justify-center border border-gray-300 dark:border-gray-600 hover:border-primaryColor rounded-lg text-gray-600 dark:text-gray-400 hover:text-white hover:bg-primaryColor transition-all duration-200 hover:shadow-md"
              >
                <FiInstagram
                  className="h-4 w-4 md:h-5 md:w-5"
                  title="Instagram"
                />
              </Link>
              <Link
                to="https://www.tiktok.com/@bossie"
                target="_blank"
                className="size-10 md:size-12 inline-flex items-center justify-center border border-gray-300 dark:border-gray-600 hover:border-primaryColor rounded-lg text-gray-600 dark:text-gray-400 hover:text-white hover:bg-primaryColor transition-all duration-200 hover:shadow-md"
              >
                <TbBrandTiktok
                  className="h-4 w-4 md:h-5 md:w-5"
                  title="TikTok"
                />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
